import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, File, X, CheckCircle } from 'lucide-react';
import { useDemoTheme } from './DemoThemeContext';

export default function FileUploadQuestion({ question, fileAnswer, onFileChange }) {
  const { isDark } = useDemoTheme();
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const handleFile = (file) => {
    setError('');
    if (!file) return;
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!question.acceptedFormats.includes(ext)) {
      setError(`Invalid format. Please upload ${question.acceptedFormats.join(' or ')}.`); return;
    }
    if (file.size > question.maxSizeMB * 1024 * 1024) {
      setError(`File exceeds ${question.maxSizeMB}MB limit.`); return;
    }
    onFileChange(question.id, { fileName: file.name, fileSize: file.size, fileType: file.type });
  };

  const handleDrop = (e) => { e.preventDefault(); setIsDragging(false); handleFile(e.dataTransfer.files[0]); };
  const handleRemove = () => { onFileChange(question.id, null); if (inputRef.current) inputRef.current.value = ''; };
  const formatSize = (bytes) => bytes < 1024 ? `${bytes} B` : bytes < 1048576 ? `${(bytes / 1024).toFixed(1)} KB` : `${(bytes / 1048576).toFixed(2)} MB`;

  return (
    <div data-testid={`file-question-${question.id}`} className="space-y-4">
      <AnimatePresence mode="wait">
        {fileAnswer?.fileName ? (
          <motion.div key="uploaded" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.97 }}
            className="rounded-xl p-5 flex items-center gap-4"
            style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.25)' }}>
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(34,197,94,0.12)' }}>
              <CheckCircle size={20} style={{ color: '#22c55e' }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate" style={{ color: 'var(--d-text-hi)' }}>{fileAnswer.fileName}</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--d-text-3)' }}>{formatSize(fileAnswer.fileSize)} · Upload successful</p>
            </div>
            <button data-testid="file-remove-btn" onClick={handleRemove}
              className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors flex-shrink-0"
              style={{ color: 'var(--d-text-3)' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#ef4444')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--d-text-3)')}>
              <X size={15} />
            </button>
          </motion.div>
        ) : (
          <motion.div key="dropzone" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            data-testid="file-dropzone"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            className="rounded-xl flex flex-col items-center justify-center gap-4 p-12 cursor-pointer transition-all duration-200"
            style={{
              background: isDragging ? 'rgba(124,58,237,0.08)' : 'var(--d-subtle)',
              border: isDragging ? '2px dashed rgba(124,58,237,0.6)' : `2px dashed var(--d-border)`,
            }}>
            <div className="w-14 h-14 rounded-xl flex items-center justify-center transition-all"
              style={{ background: isDragging ? 'rgba(124,58,237,0.15)' : 'var(--d-hover)' }}>
              <Upload size={24} style={{ color: isDragging ? '#a78bfa' : 'var(--d-text-3)' }} />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium" style={{ color: isDragging ? '#7c3aed' : 'var(--d-text-1)' }}>
                {isDragging ? 'Drop file here' : 'Drag & drop or click to browse'}
              </p>
              <p className="text-xs mt-1" style={{ color: 'var(--d-text-3)' }}>
                {question.acceptedFormats.join(', ')} · Max {question.maxSizeMB}MB
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {error && (
        <motion.p initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} className="text-xs" style={{ color: '#f87171' }} data-testid="file-error">
          {error}
        </motion.p>
      )}

      <input ref={inputRef} type="file" accept={question.acceptedFormats.join(',')} className="hidden" onChange={(e) => handleFile(e.target.files[0])} />

      <div className="flex items-center gap-2 flex-wrap">
        {question.acceptedFormats.map((fmt) => (
          <span key={fmt} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-mono"
            style={{ background: 'var(--d-subtle)', border: '1px solid var(--d-border-sub)', color: 'var(--d-text-3)' }}>
            <File size={11} />{fmt}
          </span>
        ))}
      </div>
    </div>
  );
}
